// @ts-strict-ignore
import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { MoveOrCopyOperationResult, ProjectNavigationService } from '../service/project-navigation.service';
import { ProjectNavigationModel, ProjectNavigationResult, ProjectNavigationTab } from '../project-navigation.model';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { CopyProjectOperationProperties } from '@project/components/project/copy/copy.component';
import { StringUtils } from '@shared/util/string-utils';
import { UserModel } from '@user/model/user.model';

@Component({
  standalone: false,  templateUrl: './copy-project.component.html',
  selector: 'app-copy-project'
})
export class CopyProjectComponent implements OnInit {
  @Input() itemToCopy: ProjectGroupVO | ProjectHeadVO;
  @Input() copyProperties: CopyProjectOperationProperties;
  @Input() initialTab: ProjectNavigationTab;
  @Input() isCompositeArticleProject: boolean;

  @Output() public finish = new EventEmitter<ProjectNavigationResult<MoveOrCopyOperationResult>>();

  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;

  public model: ProjectNavigationModel;

  public submitEnabled = false;

  public lastSelectedGroupIdToMoveOrCopy: number;

  public constructor(
    private projectNavigationService: ProjectNavigationService,
    private userModel: UserModel
  ) {}

  public ngOnInit() {
    this.lastSelectedGroupIdToMoveOrCopy = this.itemToCopy instanceof ProjectGroupVO ? this.itemToCopy.id : this.itemToCopy.projectGroup.id;
    this.model = new ProjectNavigationModel();
    this.model.compositeCheckBoxValue = this.isCompositeArticleProject;
    this.model.newConfigName = this.itemToCopy instanceof ProjectGroupVO ? this.itemToCopy.groupName : this.itemToCopy.name;
    this.model.initialTab = this.initialTab;
    this.model.currentModel = {
      importType: null,
      lastSelectedGroupId: this.lastSelectedGroupIdToMoveOrCopy,
      isMoveOrCopy: true,
      preselectGroup: true,
      visibleSharedProjects: true,
      visibleSaveBlock: true,
      visibleHiddenProjects: true
    };

    this.model.canNavigateIntoProjectGroups =  this.itemToCopy instanceof ProjectHeadVO;

    this.projectNavigationService.openPreselectedProjectGroup(this.model);
  }

  public cancel() {
    this.finish.emit({success: true, result: undefined});
  }

  public updateSubmitEnabled() {
    const copyToProjectGroup = this.getCopyToProjectGroup()

    if (copyToProjectGroup != null && copyToProjectGroup.id < 0 && copyToProjectGroup.groupOwner.userId !== this.userModel.user.id) {
      this.submitEnabled = false;
    } else {
      this.submitEnabled = !!copyToProjectGroup && this.existsNewConfigName();
    }
  }

  private existsNewConfigName(): boolean {
    return StringUtils.isNotEmpty(this.model.newConfigName);
  }

  public async copyProject() {
    const res = await this.projectNavigationService
      .copyProjectOrConfiguration(this.model, this.copyProperties, this.itemToCopy, this.getCopyToProjectGroup(), this.model.newConfigName);
    if (!!res) {
      this.finish.emit({success: true, result: res});
    } else {
      this.finish.emit({success: false});
    }
  }

  private getCopyToProjectGroup(): ProjectGroupVO {
    if (this.itemToCopy instanceof ProjectGroupVO) {
      if (ProjectGroupUtils.isFolderGroup(this.itemToCopy)) {
        return this.getCopyToProjectGroupForFolder(this.itemToCopy);
      } else {
        return this.getCopyToProjectGroupForProjectGroup();
      }
    } else {
      return this.getCopyToProjectGroupForProject();
    }
  }

  private getCopyToProjectGroupForProject() {
    // copy configuration into the selected project or to the project selected configuration belongs to
    if (this.model.currentSelectedItem instanceof ProjectHeadVO) {
      return this.model.currentSelectedItem.projectGroup;
    }
    if (this.isSelectedConfigurationGroup()) {
      return this.model.currentSelectedItem;
    }
    return this.model.currentViewedProjectGroup;
  }

  private getCopyToProjectGroupForProjectGroup() {
    // copy project into the selected folder or to the opened one
    if (this.isSelectedDefaultGroup() && this.model.currentSelectedItem instanceof ProjectGroupVO) {
      return this.model.currentSelectedItem;
    } else if (this.model.currentProjectFolder) {
      return this.model.currentProjectFolder;
    }
    return null;
  }

  private getCopyToProjectGroupForFolder(folderProjectGroup: ProjectGroupVO): ProjectGroupVO {
    // check if the given folder can be copied into another (selected) folder
    if (this.isSelectedDefaultGroup() && this.model.currentSelectedItem instanceof ProjectGroupVO) {
      const isCopiedGroupSelected = this.model.currentSelectedItem.id === this.itemToCopy.id;
      const isChildGroupSelected = ProjectGroupUtils.isTargetProjectGroupChildOfProjectGroup(this.model.currentSelectedItem, folderProjectGroup);
      if (!isCopiedGroupSelected && !isChildGroupSelected) {
        return this.model.currentSelectedItem;
      }
    }
    // check if the given folder can be copied into opened folder
    if ((this.isSelectedConfigurationGroup() || this.isSelectedProject() || this.isNothingSelected()) && this.model.currentProjectFolder) {
      const isCopiedGroupOpened = this.model.currentProjectFolder.id === this.itemToCopy.id;
      const isChildGroupOpened = ProjectGroupUtils.isTargetProjectGroupChildOfProjectGroup(this.model.currentProjectFolder, folderProjectGroup);
      if (!isCopiedGroupOpened && !isChildGroupOpened) {
        return this.model.currentProjectFolder;
      }
    }
    return null;
  }

  private isSelectedDefaultGroup(): boolean {
    return (
      this.model.currentSelectedItem &&
      this.model.currentSelectedItem instanceof ProjectGroupVO &&
      ProjectGroupUtils.isFolderGroup(this.model.currentSelectedItem)
    );
  }

  private isSelectedConfigurationGroup(): boolean {
    return this.model.currentSelectedItem instanceof ProjectGroupVO && ProjectGroupUtils.isConfigurationGroup(this.model.currentSelectedItem);
  }

  private isSelectedProject(): boolean {
    return this.model.currentSelectedItem instanceof ProjectHeadVO;
  }

  private isNothingSelected(): boolean {
    return !this.model.currentSelectedItem;
  }
}
